/**
 * Modal for uploading NAR / auth-letter PDFs, selecting J-codes, and running the upload pipeline.
 * @module narLetterModal
 */

import { Modal, TextInput } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getDocumentTitleByDocType } from "../../../constants/statusToDocMap/statusToDocMap";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { DocumentTypesForOrder } from "../../../enums/documentTypes";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import InlineLoaderWithMessage from "../../inlineLoaderWithMessage/inlineLoaderWithMessage";
import DocumentsList from "./components/documentsList";
import FileUploadSection from "./components/fileUploadSection";
import MedicineSelection from "./components/medicineSelection";
import { useDocMetaData } from "./hooks/useDocMetaData";
import { useFileUploadPipeline } from "./hooks/useFileUploadPipeline";
import { useNarLetterEvents } from "./hooks/useNarLetterEvents";
import { NarLetterModalMetaData, NarLetterModalProps } from "./types";
import { handleDocumentSelection } from "./utils/handleDocumentSelection";

/**
 * Composes doc metadata, analytics, merge/upload, and Postgres J-code updates for letter uploads.
 *
 * @param props - Modal props from `NarLetterModalProps` (close, optional drag-drop, logging, success).
 */
const NarLetterModal = (props: NarLetterModalProps) => {
  const { generalOrderDetails, authOnFileDetails } =
    useDataAccumulatorForSingleOrder();
  const { metaData: rawMetaData } = useSelector(
    (state: RootState) => state.modal,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const orgID = getOrgIdForFetchExternalWorklist();

  const metaData = rawMetaData as NarLetterModalMetaData | undefined;
  const latestFetchIdRef = useRef(0);
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const [documentError, setDocumentError] = useState<string | null>(null);

  useEffect(
    () => () => {
      ++latestFetchIdRef.current;
    },
    [],
  );
  const uploadAbortControllerRef = useRef<AbortController | null>(null);

  const { docMetaData, selectedMedicines, handleMedicineSelection } =
    useDocMetaData({
      orderId: generalOrderDetails?.order?.order_id ?? "",
      metaData,
      authOnFileDetails: authOnFileDetails?.auth_on_file ?? null,
    });

  const events = useNarLetterEvents({
    generalOrderDetails: generalOrderDetails?.order ?? null,
    userId: user?.id ?? "",
    userEmail: user?.email ?? "",
    orgID: orgID ?? "",
    payerName,
    metaData,
    shouldLogEvent: props.shouldLogEvent,
  });

  const {
    fileNames,
    uploadedFiles,
    isUploading,
    isMerging,
    documentTitle,
    setDocumentTitle,
    progress,
    fileInputRef,
    handleOnSave,
    handleFileChange,
    handleFileDelete,
    addFileFromStorage,
    handleDismiss,
  } = useFileUploadPipeline({
    authOnFileDetails: authOnFileDetails?.auth_on_file ?? null,
    generalOrderDetails: generalOrderDetails?.order ?? null,
    metaData,
    selectedMedicines,
    payerName,
    userEmail: user?.email ?? "",
    onSuccess: props.onSuccess,
    onAgentRerunStatusChange: props.onAgentRerunStatusChange,
    onClose: () => {
      ++latestFetchIdRef.current;
      setIsDocumentLoading(false);
      setDocumentError(null);
      uploadAbortControllerRef.current?.abort();
      props.onClose();
    },
    onDocUploaded: events.logDocUploaded,
    abortControllerRef: uploadAbortControllerRef,
  });

  useEffect(() => {
    console.log("[documentSelection] modal state", {
      fetchId: latestFetchIdRef.current,
      fileCount: uploadedFiles.length,
      displayedFileCount: fileNames.length,
      loading: isDocumentLoading,
      hasError: Boolean(documentError),
    });
  }, [
    uploadedFiles.length,
    fileNames.length,
    isDocumentLoading,
    documentError,
  ]);

  const selectDocument = (value: string) => {
    void handleDocumentSelection({
      value,
      documentsList: metaData?.documentsList,
      defaultLabel: metaData?.label,
      latestFetchIdRef,
      addFileFromStorage,
      setDocumentTitle,
      setIsDocumentLoading,
      setDocumentError,
    });
  };

  return (
    <Modal
      dialogId={ModalId.NAR_LETTER_MODAL}
      title={getDocumentTitleByDocType(metaData?.docType ?? "")}
      onClose={handleDismiss}
      onCancel={handleDismiss}
      onSave={handleOnSave}
      saveButtonText="Save"
      cancelText="Cancel"
      disableSave={
        isDocumentLoading ||
        Boolean(documentError) ||
        isUploading ||
        isMerging ||
        !documentTitle.trim() ||
        selectedMedicines.length === 0 ||
        uploadedFiles.length === 0
      }
      maxHeightPercentage={80}
    >
      <div className="flex flex-col gap-5 p-1">
        {metaData?.docType === DocumentTypesForOrder.AUTH_LETTER && (
          <>
            {metaData?.documentsLoading ? (
              <InlineLoaderWithMessage
                message="Loading documents..."
                className="flex w-full items-center justify-center"
              />
            ) : (
              <div className="documents-list-container-for-order w-full border-b border-primaryGray-15">
                <DocumentsList
                  documentsList={metaData?.documentsList ?? []}
                  label={metaData?.label}
                  onChange={selectDocument}
                  className="pb-4"
                />
              </div>
            )}
          </>
        )}
        {isDocumentLoading && (
          <div
            role="status"
            aria-live="polite"
            className="flex w-full items-center justify-center"
          >
            <InlineLoaderWithMessage message="Loading selected document..." />
          </div>
        )}
        {documentError && (
          <div role="alert" className="text-sm text-red-600">
            <p>{documentError}</p>
          </div>
        )}
        <TextInput
          id="document-title-input"
          label="Document Title (Enter as per OncoEMR)"
          onChange={(data) => {
            setDocumentTitle(data.value);
            events.logDocumentTitleUpdated(data.value);
          }}
          defaultValue={documentTitle}
        />
        <MedicineSelection
          docMetaData={docMetaData}
          selectedMedicines={selectedMedicines}
          onMedicineSelection={handleMedicineSelection}
          onCheckBoxEvent={(medicineName, isChecked) =>
            events.logCheckBoxClick(medicineName, isChecked, docMetaData)
          }
        />
        <FileUploadSection
          showDragDropUpload={props.showDragDropUpload}
          fileNames={fileNames}
          progress={progress}
          isUploading={isUploading}
          fileInputRef={fileInputRef}
          onFileChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            console.log("[manualFileUpload] change received", {
              fetchId: latestFetchIdRef.current,
              selectedFileCount: files.length,
              existingFileCount: uploadedFiles.length,
              displayedFileCount: fileNames.length,
              selectedFileSizes: files.map((file) => file.size),
              documentLoading: isDocumentLoading,
              hasDocumentError: Boolean(documentError),
            });
            const hasDuplicate = files.some((file) =>
              uploadedFiles.some((existing) => existing.name === file.name),
            );
            console.log("[manualFileUpload] duplicate check", {
              hasDuplicate,
              hasSelectedFiles: files.length > 0,
            });
            if (files.length && !hasDuplicate) {
              const previousFetchId = latestFetchIdRef.current;
              ++latestFetchIdRef.current;
              setIsDocumentLoading(false);
              setDocumentError(null);
              console.log("[manualFileUpload] document selection invalidated", {
                previousFetchId,
                fetchId: latestFetchIdRef.current,
                loadingResetScheduled: true,
                errorClearScheduled: true,
              });
            } else {
              console.log("[manualFileUpload] document selection retained", {
                fetchId: latestFetchIdRef.current,
                reason: hasDuplicate ? "duplicate-file" : "no-files-selected",
              });
            }
            console.log("[manualFileUpload] calling handleFileChange");
            try {
              handleFileChange(event);
              console.log("[manualFileUpload] handleFileChange returned", {
                fetchId: latestFetchIdRef.current,
                expectedOutcome: hasDuplicate
                  ? "duplicate-rejected"
                  : files.length > 0
                    ? "file-state-updates-scheduled"
                    : "no-files-to-add",
              });
            } catch (error) {
              console.log("[manualFileUpload] handleFileChange threw", {
                fetchId: latestFetchIdRef.current,
                errorName: error instanceof Error ? error.name : "UnknownError",
              });
              throw error;
            }
          }}
          onFileDelete={handleFileDelete}
          onUploadClick={events.logUploadDocument}
          onDeleteClick={events.logDeleteDocument}
        />
      </div>
    </Modal>
  );
};

export default NarLetterModal;
